import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Inline formatting: render **bold** (content is our own trusted markdown, no user input).
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
};

const parseRow = (row) => row.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());

// Lightweight markdown renderer for the legal documents: headings, bold, bullet/ordered
// lists, tables and horizontal rules. Keeps us off a heavy markdown dependency.
const renderMarkdown = (content) => {
  const lines = content.split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) { i++; continue; }

    // Top-level "# " heading = document title, already shown above — skip to avoid duplication.
    if (trimmed.startsWith('# ')) { i++; continue; }
    if (trimmed.startsWith('### ')) {
      blocks.push(<h3 key={key++} className="text-lg font-semibold text-white mt-6 mb-2">{renderInline(trimmed.slice(4))}</h3>);
      i++; continue;
    }
    if (trimmed.startsWith('## ')) {
      blocks.push(<h2 key={key++} className="text-xl font-bold text-white mt-8 mb-3">{renderInline(trimmed.slice(3))}</h2>);
      i++; continue;
    }
    if (trimmed === '---') { blocks.push(<hr key={key++} className="my-6 border-[#1f2937]" />); i++; continue; }

    // Table: consecutive lines starting with "|" (2nd line is the |---| separator).
    if (trimmed.startsWith('|')) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) { rows.push(lines[i].trim()); i++; }
      const header = parseRow(rows[0]);
      const body = rows.slice(2).map(parseRow);
      blocks.push(
        <div key={key++} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>{header.map((c, j) => (
                <th key={j} className="text-left text-white font-semibold border border-[#1f2937] px-3 py-2 bg-[#0d0d0d]">{renderInline(c)}</th>
              ))}</tr>
            </thead>
            <tbody>{body.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => (
                <td key={ci} className="border border-[#1f2937] px-3 py-2 align-top">{renderInline(c)}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // Unordered list
    if (trimmed.startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) { items.push(lines[i].trim().slice(2)); i++; }
      blocks.push(
        <ul key={key++} className="list-disc pl-6 mb-4 space-y-1">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) { items.push(lines[i].trim().replace(/^\d+\.\s/, '')); i++; }
      blocks.push(
        <ol key={key++} className="list-decimal pl-6 mb-4 space-y-1">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ol>
      );
      continue;
    }

    // Paragraph
    blocks.push(<p key={key++} className="mb-4 leading-relaxed">{renderInline(trimmed)}</p>);
    i++;
  }
  return blocks;
};

const DocumentPage = () => {
  const { slug } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const res = await axios.get(`${API}/documents/${slug}`);
        setDocument(res.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchDocument();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Dokument nie znaleziony</h1>
          <Link to="/" className="btn-primary">Wróć na stronę główną</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] min-h-screen">
      <Navbar />
      <main className="section-padding pt-32">
        <div className="container-custom max-w-3xl">
          <h1 className="text-4xl font-bold text-white mb-8" data-testid="document-title">{document.title}</h1>
          <div className="max-w-none text-[#9CA3AF]" data-testid="document-content">
            {renderMarkdown(document.content)}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DocumentPage;